/**
 * Phase 8 — Typed query key factory.
 *
 * Single source of truth for every TanStack Query cache key in the app.
 * Replaces ad-hoc string-array keys scattered across hooks. Benefits:
 *  - Type-safe invalidation: `queryClient.invalidateQueries({ queryKey: qk.manufacturers.all })`
 *  - Hierarchical: invalidating `qk.manufacturers.all` invalidates every sub-key.
 *  - Discoverable: one file shows the full cache shape.
 */

export const qk = {
  manufacturers: {
    all:        ["manufacturers"] as const,
    list:       () => [...qk.manufacturers.all, "list"] as const,
    byType:     (type: "lamp" | "furniture") => [...qk.manufacturers.all, "type", type] as const,
    detail:     (slug: string) => [...qk.manufacturers.all, "detail", slug] as const,
    links:      (id: string) => [...qk.manufacturers.all, "links", id] as const,
    categories: (id: string) => [...qk.manufacturers.all, "categories", id] as const,
    products:   (id: string) => [...qk.manufacturers.all, "products", id] as const,
    collections:(id: string) => [...qk.manufacturers.all, "collections", id] as const,
    byCategory: (slug: string) => [...qk.manufacturers.all, "byCategory", slug] as const,
  },
  products: {
    all:        ["products"] as const,
    byCategory: (slug: string, mfrId?: string) =>
                  [...qk.products.all, "byCategory", slug, mfrId ?? null] as const,
  },
  collections: {
    all:        ["collections"] as const,
    byCategory: (slug: string, mfrId?: string) =>
                  [...qk.collections.all, "byCategory", slug, mfrId ?? null] as const,
    images:     (categorySlug: string) =>
                  [...qk.collections.all, "images", categorySlug] as const,
  },
} as const;

/**
 * Stale-time tokens, route-aligned.
 * Catalog data changes rarely (manual content edits) so we lean long.
 */
export const STALE = {
  /** 5 min — hot lists rendered on every page (manufacturer scroll, nav data). */
  short:  5 * 60 * 1000,
  /** 30 min — detail pages and slideshow pools. */
  medium: 30 * 60 * 1000,
  /** 1 h — taxonomy/static-ish data. */
  long:   60 * 60 * 1000,
} as const;
